<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Dosare succesorale
      <resdps-help-tip :text="fileHelp" position="is-bottom" />
      <template #right>
        <div class="buttons">
          <succession-help class="mr-2" />
          <b-tooltip
            :label="cdpsHelp"
            :delay="500"
            type="is-dark"
            position="is-bottom"
            multilined
            class="action-tip"
          >
            <b-button
              icon-left="file-download-outline"
              :loading="downloadingCdps"
              :disabled="downloadingCdps"
              @click="downloadCdps"
            >
              Formular C.D.P.S.
            </b-button>
          </b-tooltip>
          <b-button type="is-primary" icon-left="plus" @click="addFile">
            Dosar nou
          </b-button>
        </div>
      </template>
    </hero-bar>
    <section class="section is-main-section">
      <succession-tabs />
      <card-component title="Dosare" icon="folder-account" class="has-table">
        <succession-files-filter
          :value="listState"
          :total="totalCount"
          @change="onFiltersChange"
        />
        <succession-files-table
          :files="files"
          :total="totalCount"
          :current-page="listState.page"
          :per-page="pageSize"
          :is-loading="isLoading"
          :today="today"
          @page-change="onPageChange"
          @open="openFile"
        />
      </card-component>
    </section>
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import {
  describeError,
  CDPS_TEMPLATE_MISSING_MESSAGE,
} from "@/helpers/resdpsErrors";
import {
  listStateFromQuery,
  queryFromListState,
  startOfDay,
  statusesForFilter,
} from "@/helpers/resdps";
import { CONCEPT_HELP, SCREEN_HELP } from "@/helpers/resdpsHelp";
import TitleBar from "@/components/TitleBar";
import HeroBar from "@/components/HeroBar";
import CardComponent from "@/components/CardComponent";
import ResdpsHelpTip from "@/components/succession/resdps-help-tip.vue";
import SuccessionHelp from "@/components/succession/succession-help.vue";
import SuccessionTabs from "@/components/succession/succession-tabs.vue";
import SuccessionFilesFilter from "@/components/succession/succession-files-filter.vue";
import SuccessionFilesTable from "@/components/succession/succession-files-table.vue";

export default {
  name: "SuccessionFiles",
  components: {
    TitleBar,
    HeroBar,
    CardComponent,
    ResdpsHelpTip,
    SuccessionHelp,
    SuccessionTabs,
    SuccessionFilesFilter,
    SuccessionFilesTable,
  },
  data() {
    return {
      fileHelp: CONCEPT_HELP.file.text,
      cdpsHelp: SCREEN_HELP.downloadCdps,
      files: [],
      totalCount: 0,
      pageSize: 20,
      isLoading: false,
      downloadingCdps: false,
      today: startOfDay(new Date()),
      titleStack: ["Succesiuni", "Dosare"],
    };
  },
  computed: {
    // Filtrele stau in adresa paginii, nu in localStorage (spec E, 5.4).
    listState() {
      return listStateFromQuery(this.$route.query);
    },
  },
  watch: {
    "$route.query"() {
      // La plecarea spre alta ruta, adresa se schimba inainte ca pagina sa dispara.
      if (this.$route.name === "succession.files") this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      const { status, year, search, page } = this.listState;
      this.isLoading = true;
      try {
        const result = await SuccessionService.listFiles({
          statuses: statusesForFilter(status),
          year,
          search,
          page,
          pageSize: this.pageSize,
        });
        this.files = result.items;
        this.totalCount = result.totalCount;
      } catch (error) {
        notify.error(this, describeError(error).message);
      } finally {
        this.isLoading = false;
      }
    },
    navigate(state) {
      this.$router.push({
        name: "succession.files",
        query: queryFromListState(state),
      });
    },
    onFiltersChange(filters) {
      this.navigate({ ...filters, page: 1 });
    },
    onPageChange(page) {
      this.navigate({ ...this.listState, page });
    },
    openFile(id) {
      this.$router.push({ name: "succession.file", params: { id } });
    },
    addFile() {
      this.$router.push({ name: "succession.add" });
    },
    // Formularul C.D.P.S. necompletat, sablonul in vigoare; blocat cat timp descarcarea e in curs,
    // ca sa nu porneasca doua cereri la un clic dublu.
    async downloadCdps() {
      if (this.downloadingCdps) return undefined;
      this.downloadingCdps = true;
      try {
        const downloaded = await SuccessionService.downloadBlankCdps();
        saveAs(downloaded.blob, downloaded.name);
      } catch (error) {
        // Doar 404-ul de la prima cerere (versiunea in vigoare) inseamna "sablonul nu e publicat";
        // serviciul marcheaza acest caz cu templateNotPublished (vezi succession.service.js). Un 404 la
        // a doua cerere (descarcarea fisierului) ar insela administratorul cu acelasi mesaj, deci trece
        // prin describeError, ca orice alta eroare.
        if (error && error.templateNotPublished) {
          notify.error(this, CDPS_TEMPLATE_MISSING_MESSAGE);
        } else {
          notify.error(this, describeError(error).message);
        }
      } finally {
        this.downloadingCdps = false;
      }
      return undefined;
    },
  },
};
</script>

<style scoped>
.buttons .action-tip:not(:last-child) {
  margin-right: 0.5rem;
}
/* Un buton dezactivat nu primeste evenimente de mouse; asa hover-ul ajunge la tooltip. */
.action-tip ::v-deep button[disabled] {
  pointer-events: none;
}
</style>
